// eslint-disable-next-line
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useLocation } from "react-router-dom"
import { Button, message } from "antd"
import { Formik } from "formik"
import { Form, Input } from "formik-antd"
import { footerText } from "util/config"
import {
  hideMessage,
  userSignIn,
  clearRequestResetUser,
} from "appRedux/actions/Auth"
import { useDocumentTitle, getItems } from "util/custom"
import IntlMessages from "util/IntlMessages"
import CircularProgress from "components/CircularProgress/index"
import { AppState } from "ReducerState"
import { isEmpty } from "lodash"
import * as yup from "yup"
import styled, { keyframes } from "styled-components"
import { FaRegEyeSlash, FaRegEye } from "react-icons/fa"
import "../styles/pages/signin-loader.css"

export const DivBlockWarning = styled.div`
  font-size: 12px;
  font-style: italic;
  color: #ef4444;
  font-weight: 400;
  margin-top: 4px;
`

/* ── Animations ── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`

/* ── Page background ── */
const PageWrap = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  align-items: center;
  justify-content: center;
  background: #f4f7f9;
  position: relative;
  overflow: hidden;
  padding: 32px 16px;
`

/* ── Card ── */
const Card = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.04);
  padding: 40px;
  animation: ${fadeInUp} 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;

  @media (max-width: 480px) {
    padding: 32px 24px;
  }
`

/* ── Logo row ── */
const LogoRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
`

const LogoImg = styled.img`
  width: 34px;
  height: 34px;
  object-fit: contain;
`

const BrandName = styled.span`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #111827;
`

const DividerLine = styled.div`
  width: 100%;
  height: 1px;
  background: #f3f4f6;
  margin-bottom: 24px;
`

const WelcomeTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  text-align: center;
  margin: 0 0 6px;
`

const WelcomeSubtitle = styled.p`
  font-size: 12px;
  color: #6b7280;
  text-align: center;
  margin: 0 0 28px;
`

/* ── Form body ── */
const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
`

const FieldWrap = styled.div`
  margin-bottom: 16px;

  .ant-input {
    height: 42px;
    border-radius: 6px;
    background: #f4f5f7;
    border: 1px solid #e2e8f0;
    transition: all 0.2s;
  }

  .ant-input:hover {
    border-color: #cbd5e1;
  }

  .ant-input:focus {
    background: #eff6ff;
    border-color: #60a5fa;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`

const PasswordWrap = styled.div`
  position: relative;

  .ant-input {
    padding-right: 40px;
  }
`

const EyeBtn = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  align-items: center;
  z-index: 2;

  &:hover {
    color: #4b5563;
  }
`

const SignInButton = styled(Button)`
  width: 100%;
  height: 42px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  background: #3b82f6 !important;
  border: none !important;
  color: #ffffff !important;
  margin-top: 8px;
  box-shadow: none !important;

  &:hover {
    background: #2563eb !important;
  }
`

const LinksRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;
  justify-content: flex-start;
`

const LinkText = styled.span`
  font-size: 12px;
  color: #3b82f6;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`

const FooterBlock = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
`

const VersionText = styled.div`
  font-size: 11px;
  color: #9ca3af;
`

const CopyrightLine = styled.div`
  font-size: 11px;
  color: #d1d5db;
`

/* ── Component ── */
const SignIn = () => {
  useDocumentTitle("Fromas Cloud")

  const { loader, alertMessage, showMessage, authUser } = useSelector(
    (state: AppState) => state.auth
  )
  const [initialData] = useState<any>({ username: undefined, password: undefined })
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const params = new URLSearchParams(location.search)
  const hotelPropCode = params.get("prop")
  const requiresPropertySuffix = isEmpty(hotelPropCode?.trim())

  const usernamePatternWithProperty = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9_.-]+$/
  const usernamePatternOptionalProperty = /^[a-zA-Z0-9._-]+(?:@[a-zA-Z0-9_.-]+)?$/

  const { access_token } = getItems<any>(["access_token"])

  useEffect(() => {
    if (showMessage) {
      if (!isEmpty(alertMessage)) {
        const msg = (alertMessage).toString().toLowerCase()
        const isSessionError =
          msg.includes('logged out') ||
          msg.includes('session') ||
          msg.includes('expired') ||
          msg.includes('unauthorized')
        if (!isSessionError) {
          message.error((alertMessage).toString())
        }
      }
      setTimeout(() => { dispatch(hideMessage()) }, 100)
    }
  }, [showMessage, alertMessage, dispatch])

  useEffect(() => {
    if (access_token) history.replace("/main/dashboard")
  }, [access_token, history])

  useEffect(() => {
    if (!loader && isAuthenticating && !authUser) setIsAuthenticating(false)
  }, [authUser, isAuthenticating, loader])

  const handleSubmit = (values: any) => {
    setIsAuthenticating(true)
    if (requiresPropertySuffix) {
      dispatch(userSignIn(values))
    } else {
      dispatch(userSignIn({ ...values, username: `${values.username}@${hotelPropCode}` }))
    }
  }

  const handleActivateClick = () => {
    dispatch(clearRequestResetUser())
    history.push('/request/activate/')
  }

  const handleResetPasswordClick = () => {
    dispatch(clearRequestResetUser())
    history.push('/request/resetpassword/')
  }

  const schema = yup.object().shape({
    username: yup
      .string()
      .required("Please input username")
      .test("username-format", "Please specify your property name like your_username@your_property", function (value) {
        if (!value) return false
        return requiresPropertySuffix
          ? usernamePatternWithProperty.test(value)
          : usernamePatternOptionalProperty.test(value)
      }),
    password: yup.string().required("Please input your password!"),
  })

  return (
    <PageWrap>
      <Card>
        {/* ── Logo row ── */}
        <LogoRow>
          <LogoImg src={`${process.env.PUBLIC_URL}/icon-512x512.png`} alt="FROMAS" />
          <BrandName>FROMAS</BrandName>
        </LogoRow>

        <DividerLine />

        {/* ── Greeting ── */}
        <WelcomeTitle>Welcome Back!</WelcomeTitle>
        <WelcomeSubtitle>Please enter your credential to sign in.</WelcomeSubtitle>

        {/* ── Form ── */}
        <Formik
          key="main-form"
          enableReinitialize
          initialValues={initialData}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched }: any) => (
            <Form layout="vertical" className="gx-signin-form gx-form-row0">
              <FieldWrap>
                <FieldLabel>Username</FieldLabel>
                <Input
                  name="username"
                  autoFocus
                  placeholder="Ex. developer@newsoft"
                />
                {errors?.username && touched.username && (
                  <DivBlockWarning>{errors.username}</DivBlockWarning>
                )}
              </FieldWrap>

              <FieldWrap>
                <FieldLabel>Password</FieldLabel>
                <PasswordWrap>
                  <Input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                  />
                  <EyeBtn type="button" onClick={() => setShowPassword(v => !v)}>
                    {showPassword ? <FaRegEye size={16} /> : <FaRegEyeSlash size={16} />}
                  </EyeBtn>
                </PasswordWrap>
                {errors?.password && touched.password && (
                  <DivBlockWarning>{errors.password}</DivBlockWarning>
                )}
              </FieldWrap>

              <SignInButton type="primary" htmlType="submit" loading={isAuthenticating}>
                <IntlMessages id="app.userAuth.signIn" />
              </SignInButton>
            </Form>
          )}
        </Formik>

        <LinksRow>
          <LinkText onClick={handleActivateClick}>
            <IntlMessages id="app.global.label.Activate" />
          </LinkText>
          <LinkText onClick={handleResetPasswordClick}>
            <IntlMessages id="app.userAuth.forgotPassword" />
          </LinkText>
        </LinksRow>

        <FooterBlock>
          <VersionText>Version {footerText.version}</VersionText>
          <CopyrightLine>{footerText.copyright}</CopyrightLine>
        </FooterBlock>
      </Card>

      {loader && (
        <div className="gx-loader-view">
          <CircularProgress className="circular-loading" />
        </div>
      )}
    </PageWrap>
  )
}

export default SignIn
